import { useCallback } from 'react';
import { backendErrorMessage, useApiCall } from './apiHelpers';
import { CheckOperationError, ValidationIssue, toValidationIssues } from '../../utils/checkOperationErrors';

export interface CheckOperationSnapshot {
  communication: {
    communicationInputId: number;
    title: string | null;
    description: string | null;
    sendStart: string | null;
    sendEnd: string | null;
    notSendable: boolean;
    channelId: number | null;
    targetTitle: string | null;
    targetDescription: string | null;
    creationPayloadPresent: boolean;
  };
  animation: {
    animationInputId: number;
    title: string | null;
    description: string | null;
  };
  offers: Array<{
    offerInputId: number;
    title: string | null;
    description: string | null;
    offerNumber: string | null;
    startValidityDate: string | null;
    endValidityDate: string | null;
    maxEditCount: number | null;
    maxUseCount: number | null;
  }>;
}

interface VerifyCommunicationResponse {
  valid: boolean;
  errors?: CheckOperationError[];
  snapshot?: CheckOperationSnapshot;
}

export type VerifyCommunicationOutcome =
  | { ok: true; snapshot: CheckOperationSnapshot | null }
  | { ok: false; message: string; issues: ValidationIssue[] };

const useVerifyCommunication = () => {
  const { authFetch, isReady, run, isLoading, error } = useApiCall(backendErrorMessage);

  const verifyCommunication = useCallback(
    async (communicationInputId: number): Promise<VerifyCommunicationOutcome> => {
      if (!isReady) return { ok: false, message: 'Session non prête, veuillez réessayer.', issues: [] };
      const outcome = await run<VerifyCommunicationResponse>(
        () => authFetch('/communication/verify', { method: 'POST', body: JSON.stringify({ communicationInputId }) }),
        "Erreur lors de la vérification de l'opération",
      );
      if (!outcome.ok) return { ok: false, message: outcome.message, issues: [] };
      const { valid, errors = [], snapshot } = outcome.data;
      if (!valid) {
        return { ok: false, message: errors[0]?.message ?? "La vérification de l'opération a échoué", issues: toValidationIssues(errors) };
      }
      return { ok: true, snapshot: snapshot ?? null };
    },
    [authFetch, isReady, run],
  );

  return { verifyCommunication, isLoading, error };
};

export default useVerifyCommunication;
